import { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { AddEntityToolbarButton } from '../../common/table/AddEntityToolbarButton';
import {
  ArticlesTableQuery,
  SupportedLanguage,
  useDeleteArticleMutation,
} from '../../../graphql/models';
import { QUERY_ARTICLES } from '../../../graphql/queries/article/articles';
import { getTranslatedTagNameOrDefault } from '../../../model/Tag';
import { QUERY_ARTICLES_TABLE } from '../../../graphql/queries/article/articles-table';
import { GenericTable } from '../../common/table/GenericTable';
import DeleteConfirmationDialog from '../../common/table/DeleteConfirmationDialog';
import React from 'react';
import { RowsToBeDeleted } from '../../common/table/types';
import {
  ResourcesTableRow,
  ResourcesTableRowWithTags,
  useResourcesTableColumns,
} from '../../common/table/resources/resources-table-columns';
import { NavigationUtil } from '../../../common/navigation/NavigationUtil';
import { ResourcePath } from '../../../common/navigation/path-types';
import { useRoutes } from '../../../common/routing/routes-hook';
import { languageCompare } from '../../../common/supported-languages';

interface Props {
  articles: ArticlesTableQuery['articles'];
}

interface ArticleTableRow extends ResourcesTableRow {
  doi: string;
  year: number;
}

type ArticleTableRowWithTags = Omit<ArticleTableRow, 'tags'> &
  ResourcesTableRowWithTags;

export const ArticlesTable = ({ articles }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { routes } = useRoutes();
  const history = useHistory();

  const [rowsToBeDeleted, setRowsToBeDeleted] = React.useState<
    RowsToBeDeleted | undefined
  >();

  const handleDeleteConfirmation = async () => {
    if (rowsToBeDeleted) {
      await deleteRows();
    }
    setRowsToBeDeleted(undefined);
  };

  const handleDeleteCancellation = () => {
    setRowsToBeDeleted(undefined);
  };

  const onAddNewArticleClick = (): void => history.push(routes.articleNew.path);

  const AddArticleButton = (): JSX.Element => (
    <AddEntityToolbarButton
      tooltipText={t('phrases.addNewArticle')}
      onClick={onAddNewArticleClick}
    />
  );

  const tableDataWithTags = articles.map(
    ({ id, authors, content, doi, year, tags }): ArticleTableRowWithTags => {
      const title =
        content.find(({ language }) => languageCompare(language, i18n))
          ?.title ?? content[0]?.title;

      const formattedAuthors = authors.map((author) => author.name);
      const languages = content
        .map((translation) => translation.language)
        .sort();

      const publicLink = NavigationUtil.publicResourceLinkFrom(
        ResourcePath.article,
        id,
        i18n.language as SupportedLanguage,
        languages
      );

      return {
        id,
        title,
        tags: tags || [],
        authors: formattedAuthors,
        doi: doi || '-',
        year,
        languages,
        publicLink,
      };
    }
  );

  const tableData: ArticleTableRow[] = tableDataWithTags.map(
    ({ tags, ...restData }) => {
      const tagsRender = tags.map((tag) => {
        return getTranslatedTagNameOrDefault(
          tag,
          i18n.language as SupportedLanguage
        );
      });

      return {
        ...restData,
        tags: tagsRender,
      };
    }
  );

  const [
    idColumn,
    titleColumn,
    tagsColumn,
    authorsColumn,
    languagesColumn,
    publicLinkColumn,
  ] = useResourcesTableColumns(tableDataWithTags);

  const columns: MUIDataTableColumn[] = [
    idColumn,
    titleColumn,
    tagsColumn,
    authorsColumn,
    {
      name: 'doi',
      label: 'DOI',
      options: {
        filterType: 'textField',
        customFilterListOptions: {
          render: (filterValue: string) => `Doi: ${filterValue}`,
        },
      },
    },
    {
      name: 'year',
      label: t('commons.year'),
      options: {
        customFilterListOptions: {
          render: (filterValue: string) =>
            `${t('commons.year')}: ${filterValue}`,
        },
      },
    },
    languagesColumn,
    publicLinkColumn,
  ];

  const [deleteArticleMutation] = useDeleteArticleMutation();

  const deleteRows = async () => {
    for (const row of rowsToBeDeleted?.data ?? []) {
      const id = articles[row.dataIndex].id;
      await deleteArticleMutation({
        variables: { id },
        refetchQueries: [
          { query: QUERY_ARTICLES },
          { query: QUERY_ARTICLES_TABLE },
        ],
      });
    }
  };

  const options: MUIDataTableOptions = {
    onRowsDelete: (rowsDeleted) => {
      setRowsToBeDeleted(rowsDeleted);
    },

    onRowClick(
      rowData: string[],
      rowMeta: { dataIndex: number; rowIndex: number }
    ) {
      history.push(
        routes.articleEdit.pathWithArguments({
          id: String(articles[rowMeta.dataIndex].id),
        })
      );
    },
    customToolbar: AddArticleButton,
    sortOrder: {
      name: 'id',
      direction: 'asc',
    },
  };

  return (
    <>
      <DeleteConfirmationDialog
        handleDeleteConfirmation={handleDeleteConfirmation}
        handleDeleteCancellation={handleDeleteCancellation}
        open={rowsToBeDeleted !== undefined}
      />
      <GenericTable
        title={t('navigation.articles')}
        data={tableData}
        columns={columns}
        options={options}
      />
    </>
  );
};
